import type { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import type { ProtocolConditionChild } from 'labstep-web/models';
import type { Metadata } from 'labstep-web/models/metadata';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import type React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

type ToolPanelContextType = {
  toolPanelState: ToolPanelState;
  setToolPanelState: React.Dispatch<
    React.SetStateAction<ToolPanelState>
  >;
  activeVariable: Partial<ProtocolConditionChild> | null;
  setActiveVariable: React.Dispatch<
    React.SetStateAction<ProtocolValue | Metadata | null>
  >;
};

const ToolPanelContext = createContext<
  ToolPanelContextType | undefined
>(undefined);

// Optional: default state if needed for initialization
// Assumes ToolPanelState.default exists
const defaultToolPanelState: ToolPanelState =
  'default' as ToolPanelState;

export const useToolPanelContext = () => {
  const context = useContext(ToolPanelContext);
  if (!context) {
    return undefined; // or throw error, depending on design
  }
  return context;
};

export const ToolPanelProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [toolPanelState, setToolPanelState] =
    useState<ToolPanelState>(defaultToolPanelState);
  const [activeVariable, setActiveVariable] = useState<
    ProtocolValue | Metadata | null
  >(null);

  const contextValue = useMemo(
    () => ({
      toolPanelState,
      setToolPanelState,
      activeVariable,
      setActiveVariable,
    }),
    [toolPanelState, activeVariable],
  );

  return (
    <ToolPanelContext.Provider value={contextValue}>
      {children}
    </ToolPanelContext.Provider>
  );
};
